import type { Editor } from '@tiptap/react';
import { notifications } from '@mantine/notifications';
import { assetUrl } from '@/services/assets.api';

/**
 * Insert freshly uploaded images into `editor`, at `position` or at the cursor.
 *
 * Uploads are awaited, and the editor can be destroyed meanwhile: switching to
 * another element mounts a fresh editor (the settings panel is keyed by
 * element), as does closing the page editor or leaving the page. Calling into
 * a destroyed editor throws ("The editor view is not available"), so check
 * first. The image is not written into the old element some other way: that
 * element may already be open in a new editor, and writing to it would
 * overwrite the user's edits. Say so instead, so the upload is not lost
 * silently.
 */
export function insertUploadedImages(
  editor: Editor | null | undefined,
  assets: Array<{ id: string }>,
  position?: number,
) {
  if (assets.length === 0) return;
  if (!editor || editor.isDestroyed) {
    notifications.show({
      color: 'yellow',
      title: assets.length === 1 ? 'Image not added' : 'Images not added',
      message: 'The upload finished after you left that text block. Add it again.',
    });
    return;
  }

  // Insert the API URL, not a presigned one: presigned URLs expire within
  // minutes and this src is persisted with the flow content.
  const chain = editor.chain().focus();
  assets.forEach((asset, i) => {
    const src = assetUrl(asset.id);
    // Offset each subsequent insert so they do not stack on one spot.
    if (position !== undefined) chain.insertContentAt(position + i, { type: 'image', attrs: { src } });
    else chain.setImage({ src });
  });
  chain.run();
}
